import { CalendarOutlined, DownOutlined, LeftOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Popover, Select } from 'antd';
import { UserRoles } from 'common/enums';
import MatiereCalendrier from 'components/matiers/MatiereCalendrier';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import './SessionsCalendarGridView.css';

const { Option } = Select;

type ViewMode = 'day' | '3day' | 'week';

interface Niche {
  date: string;
  level: { _id: string; title: string; levelGroup: string };
  topic: { _id: string; title: string; description: string };
  timeRanges: string[];
  idCreneau: string;
  reservations: any[];
  status: string;
  annonce: any;
  id: number;
  creneauTypes: string | string[];
  grouplevels: string;
  day: string;
  classeId: string;
  classeTitle: string;
}

interface DayData { day: string; niches: Niche[] }

interface Session {
  day: string;
  range: string[];
  title?: string;
  level?: any;
  topic?: any;
  classeId?: any;
  classeTitle?: string;
}

interface SessionsCalendarGridViewProps {
  sessions?: Session[];
  data?: any;
  dayData?: DayData[];
  showFilter: boolean;
  showParticular?: boolean;
  showAll?: boolean;
  isTeacherCalendar?: boolean;
}

const convertDayDataToSessions = (dayData: DayData[]): Session[] => {
  const sessions: Session[] = [];
  dayData.forEach((dayItem) => {
    const nichesByIdCreneau = new Map<string, Niche[]>();
    dayItem.niches.forEach((niche) => {
      const existing = nichesByIdCreneau.get(niche.idCreneau) || [];
      existing.push(niche);
      nichesByIdCreneau.set(niche.idCreneau, existing);
    });
    nichesByIdCreneau.forEach((niches) => {
      niches.sort((a, b) => a?.timeRanges[0].localeCompare(b?.timeRanges[0]));
      const allTimeRanges: string[] = [];
      niches.forEach((niche) => {
        niche.timeRanges.forEach((time) => {
          if (!allTimeRanges.includes(time)) allTimeRanges.push(time);
        });
      });
      allTimeRanges.sort((a, b) => a?.localeCompare(b));
      sessions.push({
        day: dayItem.day,
        range: allTimeRanges,
        title: niches[0]?.topic.title,
        level: niches[0]?.level,
        topic: niches[0]?.topic,
        classeId: dayItem?.niches[0]?.classeId || "",
        classeTitle: dayItem?.niches[0]?.classeTitle || "",
      });
    });
  });
  return sessions;
};

const SessionsCalendarGridView: React.FC<SessionsCalendarGridViewProps> = ({
  sessions: propSessions,
  data,
  dayData,
  showFilter,
  showParticular,
  showAll,
  isTeacherCalendar
}) => {
  const sessions = useMemo(() => {
    if (dayData) return convertDayDataToSessions(dayData);
    return propSessions || [];
  }, [dayData, propSessions]);

  // FIX: proper resize listener with cleanup
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Default to 'day' on mobile, 'week' on desktop
  const [viewMode, setViewMode] = useState<ViewMode>(window.innerWidth <= 768 ? 'day' : 'week');
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const calendarBodyRef = useRef<HTMLDivElement>(null);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const { user } = useSelector((state) => state.auth);
  const isTeacher = user?.roles?.includes(UserRoles?.TEACHER);
  const isOrganism = user?.roles?.includes(UserRoles?.ORGANISME);

  useEffect(() => {
    if (sessions) setFilteredData(sessions);
  }, [sessions]);

  const handleFiltering = (value: string) => {
    setFilter(value);
    if (!value) { setFilteredData(sessions); return; }
    let filtered = sessions;
    if (value === "thisClass") filtered = sessions.filter((s: any) => s.classeId && data?._id && s.classeId === data._id);
    if (value === "otherClasses") filtered = sessions.filter((s: any) => s.classeId && data?._id && s.classeId !== data._id);
    if (value === "particular") filtered = sessions.filter((s: any) => !s.classeId);
    if (value === "all") filtered = sessions;
    setFilteredData(filtered);
  };

  const [currentDate, setCurrentDate] = useState(
    filteredData.length > 0 ? new Date(filteredData[0]?.day) : new Date()
  );

  const formatDate = () => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const today = new Date();
    const isToday = currentDate.getDate() === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();
    const day = days[currentDate.getDay()];
    const month = months[currentDate.getMonth()];
    if (isToday) return `Aujourd'hui, ${currentDate.getDate()} ${month} ${currentDate.getFullYear()}`;
    return `${day}, ${currentDate.getDate()} ${month} ${currentDate.getFullYear()}`;
  };

  const getWeekDays = () => {
    const week = [];
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = currentDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(currentDate.getDate() + diff);
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // NEW: 3-day view — currentDate and next 2 days
  const get3Days = () => {
    const days = [];
    for (let i = 0; i < 3; i++) {
      const day = new Date(currentDate);
      day.setDate(currentDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getDayName = (date: Date) => {
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    return days[date.getDay() === 0 ? 6 : date.getDay() - 1];
  };

  const getMonthAbbr = (date: Date) => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return months[date.getMonth()];
  };

  const timeSlots = Array.from({ length: 16 }, (_, i) => i + 8);

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') newDate.setDate(currentDate.getDate() - 7);
    else if (viewMode === '3day') newDate.setDate(currentDate.getDate() - 3);
    else newDate.setDate(currentDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') newDate.setDate(currentDate.getDate() + 7);
    else if (viewMode === '3day') newDate.setDate(currentDate.getDate() + 3);
    else newDate.setDate(currentDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => setCurrentDate(new Date());

  const handleScrollUp = () => calendarBodyRef.current?.scrollBy({ top: -200, behavior: 'smooth' });
  const handleScrollDown = () => calendarBodyRef.current?.scrollBy({ top: 200, behavior: 'smooth' });

  const getMonthName = () => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return months[currentDate.getMonth()];
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const calculateSessionHeight = (session: Session): number => {
    const startTime = session.range[0];
    const endTime = session.range[session.range.length - 1];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    return ((endHour * 60 + endMinute) - (startHour * 60 + startMinute)) / 60;
  };

  const getSessionsStartingAtHour = (day: Date, hour: number) => {
    return filteredData.filter((session) => {
      const sessionDate = new Date(session.day);
      const dayMatches = sessionDate.getDate() === day.getDate() &&
        sessionDate.getMonth() === day.getMonth() &&
        sessionDate.getFullYear() === day.getFullYear();
      if (!dayMatches) return false;
      return parseInt(session.range[0].split(':')[0]) === hour;
    });
  };

  const getDaySessionsWithHeight = (day: Date) => {
    return filteredData
      .filter((session) => {
        const sessionDate = new Date(session.day);
        return sessionDate.getDate() === day.getDate() &&
          sessionDate.getMonth() === day.getMonth() &&
          sessionDate.getFullYear() === day.getFullYear();
      })
      .map((session) => ({
        ...session,
        startHour: parseInt(session.range[0].split(':')[0]),
        startMinute: parseInt(session.range[0].split(':')[1]),
        durationHours: calculateSessionHeight(session),
      }));
  };

  const capitalize = (str?: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const getSessionTitle = (session: any) => {
    if (session?.typeCreneaux === "individuel") {
      return isTeacher
        ? capitalize(session?.studId?.firstname) + " " + capitalize(session?.studId?.lastname)
        : capitalize(session?.profId?.firstname) + " " + capitalize(session?.profId?.lastname);
    }
    return capitalize(session?.title) || capitalize(session?.topic?.title) || capitalize(data?.title) || capitalize(data?.classeTitle) || capitalize(session?.classeTitle) || 'Session';
  };

  const getSessionTeacher = (session: any) =>
    capitalize(session?.profId?.firstname + " " + session?.profId?.lastname) || '';

  const getSessionBackground = (session: any) => {
    if (session?.typeCreneaux === "individuel") return "#ffdede";
    if (session?.typeCreneaux && session?.typeCreneaux !== "individuel") {
      return session?.classeId && session?.classeId === data?._id ? "#c4dfe0" : "#ffddab";
    }
    return "#c4dfe0";
  };

  const getSessionBackgroundColor = (session: any) =>
    session?.classeId && session?.classeId === data?._id ? "#c4dfe0" : "#ffddab";

  useEffect(() => {
    if (filteredData) {
      setCurrentDate(filteredData.length > 0 ? new Date(filteredData[0]?.day) : new Date());
    }
  }, [filteredData]);

  // Shared controls — rendered in both day and multi-day views
  const renderControls = () => (
    <div className="calendar-controls">
      <div className="calendar-navigation">
        <div className="year-box">
          <Button icon={<LeftOutlined />} onClick={handlePrevious} className="nav-button" />
          <Button icon={<CalendarOutlined />} onClick={handleToday} className="today-button">
            {isMobile ? `${currentDate.getDate()} ${getMonthAbbr(currentDate)}` : formatDate()}
          </Button>
          <Button icon={<RightOutlined />} onClick={handleNext} className="nav-button" />
        </div>
        {!isMobile && (
          <div className="month-box">
            <Button icon={<LeftOutlined />} onClick={handlePreviousMonth} className="nav-button" />
            <Button className="month-button">{getMonthName()}</Button>
            <Button icon={<RightOutlined />} onClick={handleNextMonth} className="nav-button" />
          </div>
        )}
      </div>

      <div className="filter-box">
        {showFilter && (
          <Select value={filter} onChange={handleFiltering} allowClear className="view-select-filter" placeholder="Filtrer">
            <Option value="thisClass"><div className="calendarFilterOption"><div className="thisClassFilter" />Cette classe</div></Option>
            <Option value="otherClasses"><div className="calendarFilterOption"><div className="otherClassFilter" />Mes autres classes</div></Option>
            {showParticular && <Option value="particular"><div className="calendarFilterOption"><div className="particularFilter" />Mes cours particuliers</div></Option>}
            {showAll && <Option value="all"><div className="calendarFilterOption"><div className="allFilter" />Tous mes cours</div></Option>}
          </Select>
        )}
        <Select value={viewMode} onChange={(value) => setViewMode(value)} className="view-select">
          <Option value="day">Jour</Option>
          <Option value="3day">3 jours</Option>
          <Option value="week">Semaine</Option>
        </Select>
      </div>
    </div>
  );

  // Shared tooltip content
  const renderTooltip = (session: any, idx: number) => (
    <div className="tooltipBox">
      {isTeacher && <img src={session?.studId?.profile} height={30} alt="" />}
      <div key={idx} style={{ background: "#FFF", gap: "10px" }}>
        <div className="session-title">{dayData ? "Créneau dispo" : getSessionTitle(session)}</div>
        <div className="session-subtitle">{getSessionTeacher(session)}</div>
        <div className="session-time">De {session.range[0]} à {session.range[session.range.length - 1]}</div>
        {session?.topic?.title && <MatiereCalendrier cours={session?.topic?.title} style={{ paddingLeft: "10px", paddingRight: "10px" }} />}
      </div>
    </div>
  );

  // ── DAY VIEW ────────────────────────────────────────────────────────────────
  if (viewMode === 'day') {
    const daySessionsWithHeight = getDaySessionsWithHeight(currentDate);

    return (
      <div className={`sessions-calendar-grid-container day-view ${isTeacherCalendar ? 'teacher-calendar' : ''}`}>
        {renderControls()}

        <div className="calendar-grid day-grid">
          <div className="day-view-date-badge">
            <div className="date-badge">
              <div className="badge-month">{getMonthAbbr(currentDate)}</div>
              <div className="badge-day">{currentDate.getDate()}</div>
              {isToday(currentDate) && <div className="badge-dot" />}
            </div>
          </div>

          <div className="day-view-scroll-controls">
            <Button icon={<UpOutlined />} onClick={handleScrollUp} className="scroll-button up-button" type="text" />
          </div>

          <div className="calendar-body day-calendar-body" ref={calendarBodyRef}>
            <div className="time-column">
              {timeSlots.map((hour) => (
                <div key={hour} className="time-slot">
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            <div className="day-sessions-column">
              {timeSlots.map((hour, hourIndex) => (
                <div key={hourIndex} className="day-time-row">
                  {daySessionsWithHeight
                    .filter(s => s.startHour === hour)
                    .map((session, idx) => (
                      <Popover key={idx} content={renderTooltip(session, idx)} placement="right">
                        <div
                          className="day-session-block"
                          style={{
                            backgroundColor: isOrganism ? getSessionBackgroundColor(session) : getSessionBackground(session),
                            height: `${session.durationHours * 70}px`,
                          }}
                        >
                          <div className="session-title">{dayData ? "Créneau dispo" : getSessionTitle(session)}</div>
                          <div className="session-time">De {session.range[0]} à {session.range[session.range.length - 1]}</div>
                        </div>
                      </Popover>
                    ))}
                </div>
              ))}
            </div>
          </div>

          <div className="day-view-scroll-controls">
            <Button icon={<DownOutlined />} onClick={handleScrollDown} className="scroll-button down-button" type="text" />
          </div>
        </div>
      </div>
    );
  }

  // ── WEEK / 3-DAY VIEW ───────────────────────────────────────────────────────
  const weekDays = viewMode === 'week' ? getWeekDays() : get3Days();
  const columnCount = weekDays.length;

  return (
    <div className={`sessions-calendar-grid-container ${isTeacherCalendar ? 'teacher-calendar' : ''}`}>
      {renderControls()}

      {/* Horizontal scroll wrapper — lets week view scroll on mobile */}
      <div className="calendar-scroll-wrapper">
        <div className="calendar-grid" style={{ minWidth: `${50 + columnCount * 90}px` }}>
          <div
            className="calendar-header-row"
            style={{ gridTemplateColumns: `50px repeat(${columnCount}, 1fr)` }}
          >
            <div className="time-column-header" />
            {weekDays.map((day, index) => (
              <div key={index} className={`day-header ${isToday(day) ? 'today' : ''}`}>
                <div className="day-name">{getDayName(day)}</div>
                <div className={`day-number ${isToday(day) ? 'today-number' : ''}`}>{day.getDate()}</div>
                {isToday(day) && <div className="today-dot" />}
              </div>
            ))}
          </div>

          <div
            className="calendar-body"
            style={{ gridTemplateColumns: `50px 1fr` }}
          >
            <div className="time-column">
              {timeSlots.map((hour) => (
                <div key={hour} className="time-slot">
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            <div className="calendar-days" style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
              {weekDays.map((day, dayIndex) => (
                <div key={dayIndex} className="day-column">
                  {timeSlots.map((hour, hourIndex) => {
                    const sessionsAtHour = getSessionsStartingAtHour(day, hour);
                    return (
                      <div key={hourIndex} className="calendar-cell">
                        {sessionsAtHour.map((session, sessionIdx) => {
                          const heightInHours = calculateSessionHeight(session);
                          return (
                            <Popover key={sessionIdx} content={renderTooltip(session, sessionIdx)} placement="right">
                              <div
                                className="session-block"
                                style={{
                                  backgroundColor: isOrganism ? getSessionBackgroundColor(session) : getSessionBackground(session),
                                  height: `${heightInHours * 70}px`,
                                  minHeight: `${heightInHours * 70}px`,
                                }}
                              >
                                <div className="session-title">{dayData ? "Créneau dispo" : getSessionTitle(session)}</div>
                                <div className="session-time">De {session.range[0]} à {session.range[session.range.length - 1]}</div>
                              </div>
                            </Popover>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsCalendarGridView;